import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControl, FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { useFormikContext } from 'formik';

import { MapsApi } from '../../../../../../../../api';
import { DD_SLASH_MMM_SLASH_YYYY } from '../../../../../../../../constants/dateFormat';
import { toFormattedBill } from '../../../../../../../../services/financial';
import Geo, { getCountryName } from '../../../../../../../../services/geo';
import { NSC_REIMBURSEMENT, NSPP_REIMBURSEMENT } from '../../../../../../../../services/i18n/namespaces';
import { getDistance } from '../services';

import AddressFields from './AddressFields/AddressFields';
import DistanceField from './DistanceField/DistanceField';
import {
  useMileageAddressesActions,
  useMileageAddressesState,
  withMileageAddressesProvider
} from './MileageAddressesContext/MileageAddressesContext';
import AddressValidator from './AddressValidator';

function MileageAddresses() {
  const { t } = useTranslation();
  const { ssuPatientDetails, mileageAddresses, syncing } = useMileageAddressesState(),
    { setAddressField, setAddressType } = useMileageAddressesActions();

  const { values, handleBlur, touched, errors } = useFormikContext();
  const { travelDate, startAddress, endAddress, distance, calculatedDistance, roundTrip } = mileageAddresses;

  useEffect(
    function() {
      if (startAddress.valid && endAddress.valid) {
        Promise.all([
          Geo.getRegion(startAddress.countryId, startAddress.regionId),
          Geo.getRegion(endAddress.countryId, endAddress.regionId)
        ]).then(function([startRegion, endRegion]) {
          MapsApi.getDistance({
            origin: {
              address: startAddress.address1,
              city: startAddress.city,
              country: getCountryName(startAddress.countryId),
              region: startRegion.localName || startRegion.name || null,
              zip: startAddress.zipCode
            },
            destination: {
              address: endAddress.address1,
              city: endAddress.city,
              country: getCountryName(endAddress.countryId),
              region: endRegion.localName || endRegion.name || null,
              zip: endAddress.zipCode
            }
          }).then(
            function({ data }) {
              setAddressField('mileageAddresses.calculatedDistance', data.distance);
            },
            () => {
              setAddressField('mileageAddresses.calculatedDistance', '');
            }
          );
        });
      } else {
        setAddressField('mileageAddresses.calculatedDistance', '');
      }
    },
    [
      endAddress.address1,
      endAddress.city,
      endAddress.countryId,
      endAddress.regionId,
      endAddress.valid,
      endAddress.zipCode,
      setAddressField,
      startAddress.address1,
      startAddress.city,
      startAddress.countryId,
      startAddress.regionId,
      startAddress.valid,
      startAddress.zipCode
    ]
  );

  return (
    <>
      <AddressValidator />
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={4} rowSpacing={0}>
          <Grid
            sx={{ backgroundColor: '#ffffff', borderRadius: '4px', border: '1px solid rgba(0, 0, 0, 0.12)' }}
            size={12}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2} sx={{ padding: '16px' }}>
                <Grid size={3}>
                  <Typography variant="subtitle1">
                    {t(`${NSPP_REIMBURSEMENT}:mileageAddresses.startAddress`)}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <FormControl>
                    <RadioGroup
                      row
                      value={startAddress.type}
                      onChange={function({ target }) {
                        setAddressType('startAddress', target.value);
                        AddressValidator.revalidate('startAddress');
                      }}
                    >
                      <FormControlLabel
                        sx={{ margin: 0 }}
                        value="PATIENT_HOME_ADDRESS"
                        control={<Radio />}
                        label={t(`${NSPP_REIMBURSEMENT}:mileageAddresses.patientHomeAddress`)}
                      />
                      <FormControlLabel sx={{ margin: 0 }} value="OTHER" control={<Radio />} label={t('other')} />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid size={3}>
                  <DesktopDatePicker
                    disableFuture={true}
                    name="travelDate"
                    value={travelDate}
                    onChange={function(date) {
                      setAddressField('mileageAddresses.travelDate', date);
                    }}
                    label={t(`${NSPP_REIMBURSEMENT}:mileageAddresses.travelDate`)}
                    format={DD_SLASH_MMM_SLASH_YYYY}
                    views={['year', 'month', 'day']}
                    slotProps={{
                      textField: {
                        variant: 'standard',
                        required: true,
                        fullWidth: true,
                        error: touched.mileageAddresses?.travelDate && Boolean(errors?.mileageAddresses?.travelDate),
                        helperText: touched.mileageAddresses?.travelDate && errors?.mileageAddresses?.travelDate,
                        onBlur: handleBlur
                      }
                    }}
                    required
                  />
                </Grid>
                <AddressFields path="startAddress" />
              </Grid>
            </Box>
          </Grid>
          <Grid
            sx={{ backgroundColor: '#ffffff', mt: 2, borderRadius: '4px', border: '1px solid rgba(0, 0, 0, 0.12)' }}
            size={12}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Grid container spacing={2} alignItems="center" sx={{ padding: '16px' }}>
                <Grid size={3}>
                  <Typography variant="subtitle1">{t(`${NSPP_REIMBURSEMENT}:mileageAddresses.endAddress`)}</Typography>
                </Grid>
                <Grid size={6}>
                  <FormControl>
                    <RadioGroup
                      row
                      value={endAddress.type}
                      onChange={function({ target }) {
                        setAddressType('endAddress', target.value);
                        AddressValidator.revalidate('endAddress');
                      }}
                    >
                      <FormControlLabel
                        sx={{ margin: 0 }}
                        value="SITE_ADDRESS"
                        control={<Radio />}
                        label={t(`${NSPP_REIMBURSEMENT}:mileageAddresses.siteAddress`)}
                      />
                      <FormControlLabel sx={{ margin: 0 }} value="OTHER" control={<Radio />} label={t('other')} />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <AddressFields path="endAddress" />
              </Grid>
            </Box>
          </Grid>
          <Grid size={12}>
            <Box sx={{ flexGrow: 1, padding: '16px 16px 0 16px' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={3}>
                  <FormControlLabel
                    label={t(`${NSPP_REIMBURSEMENT}:mileageAddresses.roundTrip`)}
                    disabled={!calculatedDistance}
                    sx={{ margin: 0 }}
                    control={
                      <Checkbox
                        checked={roundTrip}
                        onChange={function(e, value) {
                          setAddressField('mileageAddresses.roundTrip', value);
                        }}
                      />
                    }
                  />
                </Grid>
                <Grid size={9}>
                  {t(`${NSPP_REIMBURSEMENT}:mileageAddresses.calculatedDistance`, {
                    value: calculatedDistance
                      ? `${getDistance(roundTrip, calculatedDistance)} ${t(`${NSC_REIMBURSEMENT}:types.Mileage`)}`
                      : t(`${NSPP_REIMBURSEMENT}:mileageAddresses.unableToCalculate`)
                  })}
                </Grid>
                <DistanceField
                  distance={distance}
                  setAddressField={setAddressField}
                  handleBlur={handleBlur}
                  error={!syncing && touched.mileageAddresses?.distance && Boolean(errors.mileageAddresses?.distance)}
                  helperText={!syncing && touched.mileageAddresses?.distance && errors.mileageAddresses?.distance}
                />
                <Grid size={9}>
                  {t(`${NSPP_REIMBURSEMENT}:mileageAddresses.calculatedReimbursement`)} {calculatedReimbursement()}
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
  function calculatedReimbursement() {
    if (ssuPatientDetails.costPerMileage) {
      return toFormattedBill(values.amount);
    }
    return (
      <Typography variant="body2" sx={{ color: 'error.main' }} component="span">
        {t(`${NSPP_REIMBURSEMENT}:mileageAddresses.noCostPerMileageIsDefined`)}
      </Typography>
    );
  }
}

export default withMileageAddressesProvider(MileageAddresses);
